import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/Spinner";
import {
  getMontageunterlageMailPreview,
  type MailFilePreview as MailFilePreviewData,
} from "@/lib/projectOverview";

function formatMailDate(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function plainTextToHtml(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n\n")
    .map((block) => `<p style="margin:0 0 0.85em 0;">${block.split("\n").map(escapeHtml).join("<br>")}</p>`)
    .join("");
}

function isFullDocument(html: string): boolean {
  return /^\s*(<!doctype\s+html|<html[\s>])/i.test(html);
}

function wrapFragment(inner: string): string {
  return `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><style>
    html,body{margin:0;padding:0;background:#fff;color:#1f2937;font-family:'Segoe UI',Calibri,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.45;word-break:break-word;}
    img{max-width:100%;height:auto;}
    table{max-width:100%;}
    blockquote{border-left:3px solid #94a3b8;margin:0.5em 0;padding-left:0.75em;color:#64748b;}
  </style></head><body><div style="margin:12px;">${inner}</div></body></html>`;
}

function mailDocument(preview: MailFilePreviewData): string {
  const html = preview.body_html?.trim() || "";
  if (html) return isFullDocument(html) ? html : wrapFragment(html);
  const text = preview.body_text?.trim() || "";
  if (!text) return "";
  return wrapFragment(plainTextToHtml(text));
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-label-tertiary">{label}</dt>
      <dd className="min-w-0 break-words text-label-secondary">{value}</dd>
    </>
  );
}

export function MailFilePreview({ orderId, itemId }: { orderId: number; itemId: number }) {
  const [preview, setPreview] = useState<MailFilePreviewData | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    void getMontageunterlageMailPreview(orderId, itemId)
      .then((next) => {
        if (!cancelled) setPreview(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Die Mail konnte nicht gelesen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, itemId]);

  useEffect(() => {
    if (!preview) {
      setBlobUrl(null);
      return;
    }
    const doc = mailDocument(preview);
    if (!doc) {
      setBlobUrl(null);
      return;
    }
    const url = URL.createObjectURL(new Blob([doc], { type: "text/html;charset=utf-8" }));
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview]);

  if (loading) {
    return (
      <div className="flex min-h-[min(50vh,32rem)] items-center justify-center rounded-xl border border-separator bg-fill-quaternary/20">
        <Spinner />
        <span className="ml-2 text-sm text-label-secondary">Mailvorschau wird geladen …</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="flex min-h-[12rem] items-center justify-center rounded-xl border border-dashed border-separator px-6 text-center text-sm text-status-red">
        {error}
      </p>
    );
  }

  const attachments = preview?.attachments?.filter(Boolean) ?? [];
  const when = formatMailDate(preview?.date);

  return (
    <section className="overflow-hidden rounded-xl border border-separator bg-card">
      <header className="border-b border-separator px-4 py-3">
        <h3 className="text-sm font-semibold text-label">{preview?.subject || "(ohne Betreff)"}</h3>
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
          {preview?.from_label ? <Fact label="Von" value={preview.from_label} /> : null}
          {preview?.to_label ? <Fact label="An" value={preview.to_label} /> : null}
          {preview?.cc_label ? <Fact label="Cc" value={preview.cc_label} /> : null}
          {when ? <Fact label="Datum" value={when} /> : null}
        </dl>
        {attachments.length ? (
          <p className="mt-2 text-xs text-label-tertiary">Anhänge: {attachments.join(", ")}</p>
        ) : null}
      </header>
      {blobUrl ? (
        <iframe
          title="Mailvorschau"
          sandbox=""
          referrerPolicy="no-referrer"
          src={blobUrl}
          className="h-[min(62vh,44rem)] w-full bg-white"
        />
      ) : (
        <p className="flex min-h-[12rem] items-center justify-center px-6 text-center text-sm text-label-secondary">
          Diese Mail enthält keinen Text.
        </p>
      )}
    </section>
  );
}
